// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState } from 'react'
import { useAuth } from '../auth/AuthContext'

/**
 * Second-factor prompt shown after a correct password when the account has MFA.
 * Consumes the mfa_token from the login challenge + a TOTP code, then completes sign-in.
 */
export default function MfaCodePrompt({ mfaToken, email, onSuccess }: {
  mfaToken: string
  email?: string
  onSuccess: () => void
}) {
  const { verifyMfa } = useAuth()
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e?: React.FormEvent) {
    e?.preventDefault()
    if (submitting || code.length < 6) return
    setSubmitting(true)
    setError(null)
    const res = await verifyMfa(mfaToken, code)
    setSubmitting(false)
    if (res.ok) { onSuccess(); return }
    setError(
      res.error === 'invalid_code' ? 'That code is incorrect or expired. Try the next one your app shows.'
      : res.error === 'mfa_challenge_invalid' ? 'This sign-in attempt timed out. Please sign in again.'
      : 'Could not verify that code. Please try again.'
    )
  }

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }} noValidate>
      <div style={{ fontSize: 14, color: 'var(--color-text-soft)' }}>
        Enter the 6-digit code from your authenticator app{email ? ` for ${email}` : ''}.
      </div>
      <input
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        maxLength={6}
        placeholder="000000"
        value={code}
        onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        style={{
          fontSize: 26, letterSpacing: 10, textAlign: 'center', padding: '12px',
          borderRadius: 8, border: '1px solid var(--color-border)', fontFamily: 'monospace',
        }}
      />
      {error && <div style={{ color: 'var(--color-danger, #dc2626)', fontSize: 13 }}>{error}</div>}
      <button
        type="submit"
        disabled={submitting || code.length < 6}
        style={{
          padding: '11px 16px', borderRadius: 8, border: 'none',
          background: 'var(--color-accent, #2563eb)', color: '#fff', fontWeight: 600,
          cursor: submitting || code.length < 6 ? 'default' : 'pointer',
          opacity: submitting || code.length < 6 ? 0.6 : 1,
        }}
      >
        {submitting ? 'Verifying…' : 'Verify & sign in'}
      </button>
    </form>
  )
}
